import { useState, useEffect } from 'react';
import { auth } from '../../firebase';
import { useRole } from '../../hooks/useRole';

const API_BASE_URL = process.env.REACT_APP_API_URL;

export default function PendingUserManager() {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { isSuperAdmin } = useRole();

  useEffect(() => {
    if (isSuperAdmin) {
      fetchPendingUsers();
    }
  }, [isSuperAdmin]);

  const fetchPendingUsers = async () => {
    try {
      setLoading(true);
      const token = await auth.currentUser.getIdToken();

      // fetch ALL users (no filter)
      const response = await fetch(`${API_BASE_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to fetch users');
      const data = await response.json();

      // Filter users that have NO customClaims (pending)
      const pending = (data.users || []).filter(u => !u.approved);
      setPendingUsers(pending);
      console.log('🔍 Data:', data);
      console.log('🔍 Pending users:', pending);
    } catch (err) {
      console.error('Error fetching pending users:', err);
      setError('Failed to load pending users');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (uid) => {
    if (!window.confirm('Approve this user?')) return;
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/users/${uid}/approve`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to approve user');

      // Remove approved user from pending list
      setPendingUsers(prev => prev.filter(user => user.uid !== uid));
      alert('✅ User approved successfully');
    } catch (err) {
      console.error('Error approving user:', err);
      alert('Error approving user');
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <h2 className="text-xl font-semibold text-red-800 mb-2">Access Denied</h2>
        <p className="text-red-700">Only Super Admins can approve pending users.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 mt-8">
      <div className="p-6 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900">Pending User Approvals</h2>
        <p className="text-gray-600 text-sm mt-1">Approve users who are waiting for access</p>
      </div>

      <div className="p-6">
        {loading ? (
          <div className="text-center py-8">
            <p className="text-gray-500">Loading pending users...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
            <button 
              onClick={fetchPendingUsers}
              className="mt-2 text-red-600 hover:text-red-700 text-sm underline"
            >
              Retry
            </button>
          </div>
        ) : pendingUsers.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">No pending users found.</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-medium text-gray-900">Email</th>
                <th className="text-left py-3 px-4 font-medium text-gray-900">Display Name</th>
                <th className="text-left py-3 px-4 font-medium text-gray-900">Created</th>
                <th className="text-left py-3 px-4 font-medium text-gray-900">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pendingUsers.map(user => (
                <tr key={user.uid} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-4 px-4">{user.email}</td>
                  <td className="py-4 px-4">{user.displayName || 'N/A'}</td>
                  <td className="py-4 px-4 text-gray-600 text-sm">
                    {user.metadata?.creationTime 
                      ? new Date(user.metadata.creationTime).toLocaleString() 
                      : 'N/A'}
                  </td>
                  <td className="py-4 px-4">
                    <button 
                      onClick={() => handleApprove(user.uid)}
                      className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
                    >
                      Approve
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
